import {inject, injectable} from 'inversify';

import type {BNCoachHistoryApiModel} from '../../../../api/types/bnrks/models';
import type {CoreCoachHistoryApiModel} from '../../../../api/types/core/models';
import type {IApiDataMapper} from '../../../../core/ports/types';
import type {Combine} from '../../../../utils/utility-types';

import {UserAdditionalDataMapperJrpc} from '../../common/UserAdditionalData/mappers/UserAdditionalData.mapper.jrpc';
import {CourseMapperJrpc} from '../../Course/mappers/Course.mapper.jrpc';
import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import {CoachHistoryFactory} from '../CoachHistory.factory';
import {CoachHistoryModel} from '../CoachHistory.model';
import {CoachHistoryExpandJrpc} from './CoachHistory.expand.jrpc';

@injectable()
export class CoachHistoryMapperJrpc
  implements
    IApiDataMapper<
      CoachHistoryMapperJrpc.ApiData,
      CoachHistoryModel.Props,
      CoachHistoryModel
    >
{
  constructor(
    @inject(CoachHistoryFactory)
    private readonly factory: CoachHistoryFactory,
    @inject(CoachHistoryExpandJrpc)
    public readonly expand: CoachHistoryExpandJrpc,
    @inject(UserAdditionalDataMapperJrpc)
    private readonly userAdditionalDataMapper: UserAdditionalDataMapperJrpc,
    @inject(UserMapperJrpc)
    private readonly userMapper: UserMapperJrpc,
    @inject(CourseMapperJrpc)
    private readonly courseMapper: CourseMapperJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
  ) {}

  mapDataToModel(
    apiData: CoachHistoryMapperJrpc.ExpandedApiData,
  ): CoachHistoryModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(
    apiData: CoachHistoryMapperJrpc.ExpandedApiData,
  ): CoachHistoryModel.Props {
    return {
      additionalData: this.additionalData(apiData),
      client: this.userMapper.mapDataToModelOrEmpty(apiData.client),
      course: this.course(apiData),
      createdDate: this.dateTimeMapper.mapToModel(apiData.created),
      currentCoach: this.userMapper.mapDataToModelOrEmpty(apiData.old_coach),
      endDate: this.dateTimeMapper.mapToModel(apiData.end_date),
      id: apiData._id,
      newCoach: this.newCoach(apiData),
      reason: apiData.reason,
      startDate: this.dateTimeMapper.mapToModel(apiData.start_date),
      status: this.status(apiData),
      type: this.type(apiData),
      updatedDate: this.dateTimeMapper.mapToModel(apiData.updated),
    };
  }

  private additionalData(
    apiData: CoachHistoryMapperJrpc.ExpandedApiData,
  ): CoachHistoryModel['additionalData'] {
    return this.userAdditionalDataMapper.mapDataToModel(
      apiData.additional_data,
    );
  }

  private course(
    apiData: CoachHistoryMapperJrpc.ExpandedApiData,
  ): CoachHistoryModel['course'] {
    return apiData.course
      ? this.courseMapper.mapDataToModel(apiData.course)
      : null;
  }

  private newCoach(
    apiData: CoachHistoryMapperJrpc.ExpandedApiData,
  ): CoachHistoryModel['newCoach'] {
    return apiData.new_coach
      ? this.userMapper.mapDataToModel(apiData.new_coach)
      : null;
  }

  private status(
    apiData: CoachHistoryMapperJrpc.ExpandedApiData,
  ): CoachHistoryModel['status'] {
    switch (apiData.status) {
      case 'assigned':
        return CoachHistoryModel.Status.ASSIGNED;
      case 'disagreed':
        return CoachHistoryModel.Status.DISAGREED;
      case 'message_sent':
        return CoachHistoryModel.Status.MESSAGE_SENT;
      case 'ongoing':
        return CoachHistoryModel.Status.ONGOING;
      case 'past':
        return CoachHistoryModel.Status.PAST;
      case 'ready_to_change':
        return CoachHistoryModel.Status.READY_TO_CHANGE;
      default:
        return CoachHistoryModel.Status.CREATED;
    }
  }

  private type(
    apiData: CoachHistoryMapperJrpc.ExpandedApiData,
  ): CoachHistoryModel['type'] {
    switch (apiData.type) {
      case 'changed_automatically':
        return CoachHistoryModel.Type.CHANGED_AUTOMATICALLY;
      case 'changed_by_coach':
        return CoachHistoryModel.Type.CHANGED_BY_COACH;
      case 'changed_by_user':
        return CoachHistoryModel.Type.CHANGED_BY_USER;
      case 'coach_coverage':
        return CoachHistoryModel.Type.COACH_COVERAGE;
      case 'matched':
        return CoachHistoryModel.Type.MATCHED;
      case 'stopped':
        return CoachHistoryModel.Type.STOPPED;
    }
  }
}

export namespace CoachHistoryMapperJrpc {
  export type ApiData = Combine<
    CoreCoachHistoryApiModel,
    BNCoachHistoryApiModel
  >;

  export type ExpandedApiData = ApiData & CoachHistoryExpandJrpc.ExpandedFields;
}
